import React, { useMemo } from 'react';
import { Modal, Steps } from 'antd';
import PrimaryButton from '../../../lib/primary-button';
import SecondaryButton from '../../../lib/secondary-button';
import checkPermission from '../../../../utils/permissions/permissions';
import { useImportAssetTypes } from './use-import-asset-types';
import { useIconUpload } from './use-icon-upload';
import { TWizardStep } from './i-import-wizard';
import UploadStep from './upload.step';
import MapColumnsStep from './map-columns.step';
import PreviewStep from './preview.step';
import IconsStep from './icons.step';
import ResultStep from './result.step';
import './import-asset-types.less';

const { Step } = Steps;

const STEP_ORDER: TWizardStep[] = ['upload', 'map', 'preview', 'icons', 'result'];

interface IImportAssetTypesModalProps {
  onClose: () => void;
  onImported: () => void;
}

const ImportAssetTypesModal = ({
  onClose,
  onImported,
}: IImportAssetTypesModalProps): JSX.Element => {
  const wizard = useImportAssetTypes();
  const { state } = wizard;
  const readOnly = !checkPermission('assetTypes', 'write');
  const createdCodes = useMemo(
    () => state.rows.filter((row) => row.status === 'created').map((row) => row.code.trim()),
    [state.rows],
  );
  const icons = useIconUpload(createdCodes);

  const finish = (): void => {
    onImported();
    onClose();
  };

  const requestClose = (): void => {
    if (state.busy) {
      return;
    }
    if (state.step === 'result') {
      finish();
      return;
    }
    if (state.rows.length === 0) {
      onClose();
      return;
    }
    Modal.confirm({
      title: 'Discard this import?',
      content: 'The file and any corrections you made will be lost.',
      okText: 'Discard',
      cancelText: 'Keep editing',
      onOk: onClose,
    });
  };

  const renderStep = (): JSX.Element | null => {
    switch (state.step) {
      case 'upload':
        return (
          <UploadStep
            fileName={state.fileName}
            fileError={state.fileError}
            parseErrors={state.table?.errors ?? []}
            busy={state.busy}
            onFileSelected={wizard.onFileSelected}
          />
        );
      case 'map':
        return state.plan && (
          <MapColumnsStep
            plan={state.plan}
            samples={wizard.samples}
            onChange={wizard.onPlanChange}
          />
        );
      case 'preview':
        return state.plan && (
          <PreviewStep
            rows={wizard.visibleRows}
            plan={state.plan}
            summary={state.summary}
            truncated={state.table?.truncated ?? false}
            onlyProblems={state.onlyProblems}
            disabled={readOnly || state.busy}
            onToggleOnlyProblems={wizard.onToggleOnlyProblems}
            onCellChange={wizard.onCellChange}
            onPropChange={wizard.onPropChange}
            onDropRow={wizard.onDropRow}
          />
        );
      case 'icons':
        return (
          <IconsStep
            phase={icons.phase}
            match={icons.match}
            progress={icons.progress}
            outcomes={icons.outcomes}
            supportsDirectory={icons.supportsDirectory}
            disabled={readOnly}
            onFilesSelected={icons.onFilesSelected}
          />
        );
      case 'result':
        return (
          <ResultStep
            summary={state.summary}
            resultSummary={state.resultSummary}
            rows={state.rows}
          />
        );
      default:
        return null;
    }
  };

  const renderFooter = (): JSX.Element => {
    if (state.step === 'result') {
      return (
        <div className="import-wizard__footer">
          <PrimaryButton type="button" title="Done" onClick={finish} />
        </div>
      );
    }

    if (state.step === 'icons') {
      const ready = icons.match?.matched.length ?? 0;

      if (icons.phase === 'done') {
        return (
          <div className="import-wizard__footer">
            <PrimaryButton type="button" title="Continue" onClick={() => wizard.goTo('result')} />
          </div>
        );
      }

      return (
        <div className="import-wizard__footer">
          <SecondaryButton type="button" title="Skip" onClick={() => wizard.goTo('result')} />
          <PrimaryButton
            type="button"
            title={ready === 1 ? 'Upload 1 icon' : `Upload ${ready} icons`}
            disabled={readOnly || ready === 0 || icons.phase !== 'ready'}
            loading={icons.phase === 'uploading'}
            onClick={() => { icons.upload(); }}
          />
        </div>
      );
    }

    const importable = state.summary.newCount;

    return (
      <div className="import-wizard__footer">
        {state.step === 'map' && (
          <SecondaryButton type="button" title="Back" onClick={() => wizard.goTo('upload')} />
        )}
        {state.step === 'preview' && (
          <SecondaryButton type="button" title="Back" onClick={() => wizard.goTo('map')} />
        )}
        <SecondaryButton type="button" title="Cancel" onClick={requestClose} />
        {state.step === 'map' && (
          <PrimaryButton
            type="button"
            title="Continue"
            disabled={!wizard.canContinueFromMap}
            onClick={() => wizard.goTo('preview')}
          />
        )}
        {state.step === 'preview' && (
          <PrimaryButton
            type="button"
            title={importable === 1 ? 'Import 1 asset type' : `Import ${importable} asset types`}
            disabled={readOnly || importable === 0}
            loading={state.busy}
            onClick={() => { wizard.submit(); }}
          />
        )}
      </div>
    );
  };

  return (
    <Modal
      visible
      centered
      destroyOnClose
      maskClosable={false}
      width="90%"
      style={{ maxWidth: 1200 }}
      title="Import Asset Types"
      onCancel={requestClose}
      footer={renderFooter()}
    >
      <Steps size="small" current={STEP_ORDER.indexOf(state.step)}>
        <Step title="Upload" />
        <Step title="Map columns" />
        <Step title="Preview & fix" />
        <Step title="Icons" />
        <Step title="Result" />
      </Steps>
      <div className="import-wizard__body">{renderStep()}</div>
    </Modal>
  );
};

ImportAssetTypesModal.displayName = 'ImportAssetTypesModal';

export default ImportAssetTypesModal;
